import { useState, useEffect } from "react";
import { X, Loader2, Settings2, AlertTriangle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { fmt, opts } from "./constants";
import { useCatalogoPresupuesto } from "./catalogoScope";

export function JornalesSettingsModal({ open, onClose, onSaved }) {
  // Fix CC (2026-09-10): dentro de un presupuesto, sus jornales (recalcula solo su copia).
  const { catApi, budgetId } = useCatalogoPresupuesto();
  const [form, setForm] = useState(null);
  const [impacto, setImpacto] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      axios.get(`${catApi}/settings/jornales-mo`, opts),
      axios.get(`${catApi}/settings/jornales-mo/impacto`, opts),
    ]).then(([j, i]) => {
      setForm(j.data);
      setImpacto(i.data.rubros_afectados || 0);
    }).catch(() => toast.error("Error al cargar los parámetros de jornal"))
      .finally(() => setLoading(false));
  }, [open, catApi]);

  if (!open) return null;

  const derivado = form ? {
    ips_diario: (form.ips_pct / 100) * form.ips_base_referencia_diaria,
    costo_hora_oficial: ((Number(form.oficial_diario) || 0) + (form.ips_pct / 100) * form.ips_base_referencia_diaria) / (Number(form.horas_jornada) || 8),
    costo_hora_ayudante: ((Number(form.ayudante_diario) || 0) + (form.ips_pct / 100) * form.ips_base_referencia_diaria) / (Number(form.horas_jornada) || 8),
  } : null;

  const handleSave = async () => {
    if (impacto > 0 && !confirm(`Este cambio recalculará el precio de ${impacto} rubro(s) de Mano de Obra que usan horas de jornal. ¿Confirmás?`)) return;
    setSaving(true);
    try {
      const { data } = await axios.put(`${catApi}/settings/jornales-mo`, {
        oficial_diario: Number(form.oficial_diario) || 0,
        ayudante_diario: Number(form.ayudante_diario) || 0,
        ips_pct: Number(form.ips_pct) || 0,
        ips_base_referencia_diaria: Number(form.ips_base_referencia_diaria) || 0,
        horas_jornada: Number(form.horas_jornada) || 8,
      }, opts);
      toast.success(`Jornales actualizados — ${data.rubros_recalculados} rubro(s) recalculado(s)`);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4" data-testid="jornales-settings-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2"><Settings2 size={16} /> Parámetros de Jornal <span className="text-xs font-semibold text-gray-400" data-testid="jornales-scope">{budgetId ? "· este presupuesto" : "· base madre"}</span></h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
        </div>
        {loading || !form ? (
          <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>
        ) : (
          <>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Jornal Oficial (Gs./día)</label>
                  <input type="number" value={form.oficial_diario} onChange={e => setForm(f => ({ ...f, oficial_diario: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm font-mono" data-testid="jornales-oficial-input" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Jornal Ayudante (Gs./día)</label>
                  <input type="number" value={form.ayudante_diario} onChange={e => setForm(f => ({ ...f, ayudante_diario: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm font-mono" data-testid="jornales-ayudante-input" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">IPS (%)</label>
                  <input type="number" value={form.ips_pct} onChange={e => setForm(f => ({ ...f, ips_pct: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm font-mono" data-testid="jornales-ips-pct-input" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Horas/jornada</label>
                  <input type="number" value={form.horas_jornada} onChange={e => setForm(f => ({ ...f, horas_jornada: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm font-mono" data-testid="jornales-horas-input" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Base de referencia IPS (Gs./día)</label>
                  <input type="number" value={form.ips_base_referencia_diaria} onChange={e => setForm(f => ({ ...f, ips_base_referencia_diaria: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm font-mono" data-testid="jornales-ips-base-input" />
                  <p className="text-[10px] text-gray-400 mt-1">Monto fijo (IPS% × base) que se suma por igual al costo/hora de Oficial y Ayudante — así está calculado en el Excel real de CEN.</p>
                </div>
              </div>
              {derivado && (
                <div className="bg-[#EBF3FB] rounded-xl p-3 text-xs space-y-1">
                  <p className="font-bold text-[#1A4B7A]">Costos derivados</p>
                  <p>Costo hora Oficial: <span className="font-bold tabular-nums">{fmt(derivado.costo_hora_oficial)}</span></p>
                  <p>Costo hora Ayudante: <span className="font-bold tabular-nums">{fmt(derivado.costo_hora_ayudante)}</span></p>
                </div>
              )}
              {impacto > 0 && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>Guardar recalculará el precio de <b>{impacto}</b> rubro(s) de Mano de Obra derivados de estos jornales.</span>
                </div>
              )}
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
                data-testid="jornales-save-btn">
                {saving ? <Loader2 size={15} className="animate-spin" /> : null}
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
